import {
  type Carer,
  type Home,
  HomeSource,
  type User,
  UserRole
} from '@lmc/ts-common'
import { InputPin } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { selfServeConstants } from 'locale/selfServe'
import Operations from 'operations'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useCreateTemplates } from '../hooks'
import {
  OnboardingButton,
  OnboardingButtonWrapper,
  OnboardingStepIndicator
} from './OnboardingButton'
import { OnboardingHeading } from './OnboardingHeading'
import { OnboardingLayout } from './OnboardingLayout'
import { OnboardingText } from './OnboardingText'

type Props = {
  homeId: string
}

export const StepPin: FC<Props> = ({ homeId }) => {
  const { t } = useTranslation(NAMESPACE.SELF_SERVE)

  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const home: Home | undefined = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const [loading, setLoading] = useState(false)

  const dispatch = useDispatch()

  const [pin, setPin] = useState<string>()

  const hasAdminCarers =
    carers?.filter((carer) => carer.role === UserRole.careHomeAdmin)?.length > 1

  const getStepPosition = () => {
    // If hasAdminCarers, step 2 was skipped, so this is step 2 of 2
    if (hasAdminCarers) {
      return { current: 2, total: 2 }
    }
    // Otherwise this is step 3 of 3
    return { current: 3, total: 3 }
  }

  const stepPosition = getStepPosition()

  const refetchCarer = async () => {
    window.location.reload()
  }

  const { createTemplateData } = useCreateTemplates(homeId, refetchCarer)

  const activatePin = async () => {
    createTemplateData()
    setLoading(true)
    const res = await dispatch(
      Operations.updateListItem({
        listId: 'carers',
        itemId: user?.id,
        successMsg: 'Set the PIN',
        // TODO double check after API update
        json: {
          pin,
          pin_confirm: pin
        }
      })
    )

    setLoading(false)
  }

  const isValidPin = pin?.length === 4

  return (
    <OnboardingLayout>
      <OnboardingHeading level={1}>
        {t(selfServeConstants.SET_A_PIN)}
      </OnboardingHeading>
      <OnboardingText>{t(selfServeConstants.YOUR_SECURE_CODE)}</OnboardingText>
      <PinWrapper>
        <InputPin onComplete={(e) => setPin(e)} />
      </PinWrapper>
      <OnboardingButtonWrapper>
        <StyledOnboardingButton
          variant="default"
          size="large"
          onClick={activatePin}
          isLoading={loading}
          disabled={!isValidPin}
        >
          {t(selfServeConstants.FINISH)}
        </StyledOnboardingButton>
        <OnboardingStepIndicator>
          {t(selfServeConstants.STEP_X_OF_X, {
            from: stepPosition.current,
            to: stepPosition.total
          })}
        </OnboardingStepIndicator>
      </OnboardingButtonWrapper>
    </OnboardingLayout>
  )
}

const PinWrapper = styled.div`
  z-index: 10;
`

const StyledOnboardingButton = styled(OnboardingButton)`
  &:disabled {
    background-color: ${({ theme }) => theme.palette.orange};
    color: ${({ theme }) => theme.palette.darkpurple};
    opacity: 0.7;
  }
`
